/**
 * LeagueNewsPage — News relevant to the user's fantasy league
 *
 * Route: /league/:id/league-news
 *
 * Filter sections:
 *   1. Frequency    (multi-select) — Daily / Weekly / Monthly
 *   2. Type of News (multi-select) — Watchlist / Matchup / Trade / League Updates
 *
 * Dev note:
 *   League News shows no news by default for unsynced/demo leagues.
 *   News populates once the user has connected a real ESPN league
 *   (i.e. league.syncStatus === 'success').
 *
 *   Currently, real-time league activity (trades, matchups, watchlist)
 *   requires authenticated ESPN Fantasy API access, which is not yet
 *   implemented. The "League Updates" category maps to the existing
 *   sorted NBA news feed as a placeholder. Other categories show
 *   appropriate empty states.
 */

import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'

// ── Constants ──────────────────────────────────────────────────────────────────

const FREQ_OPTS = [
  { key: 'daily',   label: 'Daily',   ms: 86_400_000,    desc: 'Refreshes daily; shows today\'s news' },
  { key: 'weekly',  label: 'Weekly',  ms: 604_800_000,   desc: 'Shows last 7 days' },
  { key: 'monthly', label: 'Monthly', ms: 2_592_000_000, desc: 'Shows last 30 days' },
]

const TYPE_OPTS = [
  {
    key: 'watchlist',
    label: 'Watchlist News',
    icon: '👀',
    desc: 'Updates on watchlisted players — acquisitions, drops, trade availability, JAI summaries',
    comingSoon: true,
  },
  {
    key: 'matchup',
    label: 'Matchup News',
    icon: '⚔️',
    desc: 'Current week matchup: lead changes, category wins/losses, waiver claims within your league',
    comingSoon: true,
  },
  {
    key: 'trade',
    label: 'Trade News',
    icon: '🔁',
    desc: 'Incoming/outgoing trade proposals and completed trades within your league',
    comingSoon: true,
  },
  {
    key: 'updates',
    label: 'League Updates',
    icon: '📋',
    desc: 'Setting changes, weekly matchup results, and ladder/standings changes',
    comingSoon: false,
  },
]

const TAG_CONFIG = {
  injury:      { icon: '🚑', label: 'INJURY',      color: 'text-red-600',   bg: 'border-transparent px-0' },
  roster:      { icon: '🔄', label: 'ROSTER',      color: 'text-blue-700',  bg: 'border-transparent px-0' },
  performance: { icon: '⭐', label: 'PERFORMANCE', color: 'text-amber-700', bg: 'border-transparent px-0' },
  news:        { icon: '📰', label: 'NEWS',        color: 'text-gray-800',  bg: 'border-transparent px-0' },
}

const DEFAULT_FILTERS = {
  frequency: ['daily'],
  types:    ['updates'],
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime()
  if (isNaN(diff)) return ''
  const m = Math.floor(diff / 60_000)
  const h = Math.floor(diff / 3_600_000)
  const d = Math.floor(diff / 86_400_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 30) return `${d}d ago`
  return `${Math.floor(d / 30)}mo ago`
}

function FilterIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M1.5 3.5A.5.5 0 0 1 2 3h12a.5.5 0 0 1 .354.854L9.5 9.207V14.5a.5.5 0 0 1-.777.416l-2-1.333A.5.5 0 0 1 6.5 13V9.207L1.646 3.854A.5.5 0 0 1 1.5 3.5z"/>
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path d="M1 4L3.5 6.5L9 1" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function LeagueNewsPage() {
  const { id }           = useParams()
  const [searchParams]   = useSearchParams()

  const [league,      setLeague]      = useState(null)
  const [articles,    setArticles]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [lastFetched, setLastFetched] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(searchParams.get('filters') === 'open')

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(`jai_league_news_filters_${id}`)
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : { ...DEFAULT_FILTERS }
    } catch { return { ...DEFAULT_FILTERS } }
  })

  const [pending, setPending] = useState(filters)

  useEffect(() => {
    leaguesApi.get(id).then(r => setLeague(r.data.league)).catch(() => {})
    fetchNews()
  }, [id])

  // ── Is this a real (synced) league? ──────────────────────────────────────────
  // League News is empty for unsynced/demo leagues per the spec.

  const isRealLeague = league?.syncStatus === 'success'

  // ── Data loading ─────────────────────────────────────────────────────────────

  const fetchNews = async (force = false) => {
    // Use shortest selected frequency as the cache TTL
    const selectedMs = filters.frequency.map(k => FREQ_OPTS.find(f => f.key === k)?.ms ?? 86_400_000)
    const freqMs     = Math.min(...selectedMs)

    if (!force) {
      try {
        const raw = localStorage.getItem(`jai_league_news_${id}`)
        if (raw) {
          const cached = JSON.parse(raw)
          if (Date.now() - new Date(cached.fetchedAt).getTime() < freqMs) {
            setArticles(cached.articles)
            setLastFetched(cached.fetchedAt)
            setLoading(false)
            return
          }
        }
      } catch {}
    }

    setLoading(true)
    setError('')
    try {
      const res       = await leaguesApi.getLeagueNews(id)
      const arts      = res.data.articles ?? []
      const fetchedAt = res.data.fetchedAt ?? new Date().toISOString()
      setArticles(arts)
      setLastFetched(fetchedAt)
      localStorage.setItem(`jai_league_news_${id}`, JSON.stringify({ articles: arts, fetchedAt }))
    } catch (err) {
      console.error('League news fetch failed:', err)
      if (articles.length === 0) setError('Failed to load league news. Tap ↻ to try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Filter actions ────────────────────────────────────────────────────────────

  const applyFilters = () => {
    setFilters(pending)
    localStorage.setItem(`jai_league_news_filters_${id}`, JSON.stringify(pending))
    setFiltersOpen(false)
  }

  const openFilters = () => {
    setPending(filters)
    setFiltersOpen(true)
  }

  // ── Client-side filter ────────────────────────────────────────────────────────

  // Determine the max age cutoff based on selected frequency (broadest window)
  const filtered = useMemo(() => {
    if (!isRealLeague) return []

    const maxMs  = Math.max(...filters.frequency.map(k => FREQ_OPTS.find(f => f.key === k)?.ms ?? 86_400_000))
    const cutoff = Date.now() - maxMs

    // Only "updates" has real data right now; other types will return empty
    const showUpdates = filters.types.includes('updates')
    if (!showUpdates) return []

    return articles.filter(a => new Date(a.published).getTime() >= cutoff)
  }, [articles, filters, isRealLeague])

  // Whether any "coming soon" only types are selected
  const hasOnlyComingSoonTypes =
    filters.types.length > 0 &&
    filters.types.every(k => TYPE_OPTS.find(o => o.key === k)?.comingSoon)

  const filterBadge =
    (filters.frequency.length > 1 || !filters.frequency.includes('daily') ? 1 : 0) +
    (filters.types.length < TYPE_OPTS.length ? 1 : 0)

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Page header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <Link
              to={`/league/${id}`}
              className="text-gray-800 hover:text-gray-900 text-sm transition mb-1.5 inline-flex items-center gap-1"
            >
              ← {league?.name ?? 'League'}
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-1.5">League News</h1>
            {error ? (
              <p className="text-red-600 text-xs mt-0.5">{error}</p>
            ) : lastFetched && isRealLeague ? (
              <p className="text-gray-800 text-xs mt-0.5">Updated {timeAgo(lastFetched)}</p>
            ) : null}
          </div>

          <div className="flex items-center gap-2 mt-1">
            {isRealLeague && (
              <button
                onClick={() => fetchNews(true)}
                disabled={loading}
                title="Refresh"
                className="text-gray-800 hover:text-gray-900 disabled:opacity-40 transition"
              >
                <span className={`text-base ${loading ? 'animate-spin inline-block' : ''}`}>↻</span>
              </button>
            )}
            <button
              onClick={openFilters}
              className="relative flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg px-3 py-1.5 transition"
            >
              <FilterIcon />
              Filters
              {filterBadge > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {filterBadge}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        {!isRealLeague ? (

          /* ── Not a real league (no sync yet) ───────────────────────── */
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🌐</span>
            </div>
            <h3 className="text-gray-900 font-semibold mb-2">No league news yet</h3>
            <p className="text-gray-700 text-sm max-w-xs mx-auto mb-5">
              League News shows trades, matchups, standings, and more — but only once you've connected a real ESPN league.
            </p>
            <Link
              to={`/league/${id}`}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl px-4 py-2 transition"
            >
              ← Back to league
            </Link>
          </div>

        ) : loading && articles.length === 0 ? (

          /* ── Loading skeleton ─────────────────────────────────────── */
          <NewsSkeleton />

        ) : hasOnlyComingSoonTypes ? (

          /* ── Only "coming soon" types selected ───────────────────── */
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-14 text-center">
            <p className="text-gray-700 text-sm font-medium mb-1">Coming soon</p>
            <p className="text-gray-800 text-xs mb-4">
              {filters.types.map(k => TYPE_OPTS.find(o => o.key === k)?.label).join(', ')} will be available once ESPN Fantasy integration is complete.
            </p>
            <button
              onClick={openFilters}
              className="text-blue-700 text-sm font-medium hover:underline"
            >
              Adjust filters →
            </button>
          </div>

        ) : filtered.length === 0 ? (

          /* ── No results after filter ─────────────────────────────── */
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-14 text-center">
            <p className="text-gray-700 text-sm font-medium mb-1">No news matches your filters</p>
            <p className="text-gray-800 text-xs mb-4">Try widening the frequency or selecting more news types.</p>
            <button
              onClick={openFilters}
              className="text-blue-700 text-sm font-medium hover:underline"
            >
              Adjust filters →
            </button>
          </div>

        ) : (

          /* ── Article list ─────────────────────────────────────────── */
          <>
            {/* Coming-soon type callout */}
            {filters.types.some(k => TYPE_OPTS.find(o => o.key === k)?.comingSoon) && (
              <div className="mb-4 flex items-start gap-2.5" style={{ borderLeft: '3px solid #9A6A00', paddingLeft: 12 }}>
                <p className="text-gray-900 text-xs leading-relaxed">
                  <strong>Watchlist, Matchup, and Trade news</strong> require ESPN Fantasy API integration — coming soon. Showing <strong>League Updates</strong> in the meantime.
                </p>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
              {filtered.map(a => <NewsItem key={a.id} article={a} />)}
            </div>
          </>
        )}
      </div>

      {/* Filter drawer */}
      <LeagueNewsFilterDrawer
        open={filtersOpen}
        filters={pending}
        onChange={setPending}
        onApply={applyFilters}
        onReset={() => setPending({ ...DEFAULT_FILTERS })}
        onClose={() => setFiltersOpen(false)}
      />
    </AppShell>
  )
}

// ── Filter Drawer ──────────────────────────────────────────────────────────────

function LeagueNewsFilterDrawer({ open, filters, onChange, onApply, onReset, onClose }) {
  const toggleFreq = (key) => {
    const current = filters.frequency
    const updated  = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key]
    // Must have at least one frequency selected
    if (updated.length > 0) onChange({ ...filters, frequency: updated })
  }

  const toggleType = (key) => {
    const current = filters.types
    const updated  = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key]
    if (updated.length > 0) onChange({ ...filters, types: updated })
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white border-l border-gray-200 z-50 flex flex-col shadow-xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-gray-900 font-semibold">League News Filters</h2>
          <button
            onClick={onClose}
            className="text-gray-800 hover:text-gray-900 transition text-2xl leading-none"
            aria-label="Close filters"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">

          {/* Section 1 — Frequency (multi-select) */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              1 — Frequency
            </p>
            <p className="text-xs text-gray-800 mb-3">
              Select all periods you want news from.
            </p>
            <div className="flex flex-col gap-2">
              {FREQ_OPTS.map(opt => {
                const active = filters.frequency.includes(opt.key)
                return (
                  <button
                    key={opt.key}
                    onClick={() => toggleFreq(opt.key)}
                    className={`flex items-start gap-3 px-3.5 py-2.5 rounded-xl text-sm text-left transition ${
                      active
                        ? 'bg-blue-700 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      active ? 'bg-white border-white' : 'border-gray-300 bg-white'
                    }`}>
                      {active && <CheckIcon />}
                    </span>
                    <div>
                      <div className="font-medium">{opt.label}</div>
                      <div className={`text-xs mt-0.5 ${active ? 'text-blue-200' : 'text-gray-800'}`}>
                        {opt.desc}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Section 2 — Type of News */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              2 — Type of News
            </p>
            <div className="flex flex-col gap-2">
              {TYPE_OPTS.map(opt => {
                const active = filters.types.includes(opt.key)
                return (
                  <button
                    key={opt.key}
                    onClick={() => !opt.comingSoon && toggleType(opt.key)}
                    className={`flex items-start gap-3 px-3.5 py-2.5 rounded-xl text-sm text-left transition ${
                      opt.comingSoon
                        ? 'bg-gray-50 border border-gray-200 cursor-default opacity-60'
                        : active
                          ? 'bg-blue-700 text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      active && !opt.comingSoon ? 'bg-white border-white' : 'border-gray-300 bg-white'
                    }`}>
                      {active && !opt.comingSoon && <CheckIcon />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span>{opt.icon}</span>
                        <span className="font-medium">{opt.label}</span>
                        {opt.comingSoon && (
                          <span className={`text-xs font-normal ml-auto flex-shrink-0 ${active && !opt.comingSoon ? 'text-blue-200' : 'text-gray-800'}`}>
                            Soon
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 leading-relaxed ${active && !opt.comingSoon ? 'text-blue-200' : 'text-gray-800'}`}>
                        {opt.desc}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            onClick={onReset}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 text-sm transition"
          >
            Reset
          </button>
          <button
            onClick={onApply}
            className="flex-1 bg-blue-700 hover:bg-blue-500 text-white font-semibold rounded-xl py-2.5 text-sm transition"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  )
}

// ── NewsItem ───────────────────────────────────────────────────────────────────

function NewsItem({ article }) {
  const tag      = TAG_CONFIG[article.tag] ?? TAG_CONFIG.news
  const citation = article.byline
    ? `${article.source} · ${article.byline}`
    : article.source

  return (
    <div className="px-5 py-4 hover:bg-gray-50 transition group">
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold border ${tag.bg} ${tag.color}`}>
          {tag.icon} {tag.label}
        </span>
        <span className="text-gray-800 text-xs">{timeAgo(article.published)}</span>
      </div>

      {article.link ? (
        <a
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-gray-900 text-sm font-semibold leading-snug mb-1.5 group-hover:text-blue-700 transition line-clamp-2"
        >
          {article.headline}
        </a>
      ) : (
        <p className="text-gray-900 text-sm font-semibold leading-snug mb-1.5 line-clamp-2">
          {article.headline}
        </p>
      )}

      {article.description && (
        <p className="text-gray-700 text-xs leading-relaxed mb-2.5 line-clamp-2">
          {article.description}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        {article.athletes?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {article.athletes.slice(0, 3).map((name, i) => (
              <span key={i} className="text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                {name}
              </span>
            ))}
            {article.athletes.length > 3 && (
              <span className="text-xs text-gray-800">+{article.athletes.length - 3}</span>
            )}
          </div>
        )}
        <span className="text-gray-800 text-xs flex-shrink-0 ml-auto">{citation}</span>
      </div>
    </div>
  )
}

// ── NewsSkeleton ───────────────────────────────────────────────────────────────

function NewsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="px-5 py-4 animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-5 w-20 bg-gray-100 rounded-md" />
            <div className="h-3 w-12 bg-gray-100 rounded" />
          </div>
          <div className="h-4 w-3/4 bg-gray-100 rounded mb-1.5" />
          <div className="h-3 w-full bg-gray-100 rounded mb-1" />
          <div className="h-3 w-2/3 bg-gray-100 rounded mb-2.5" />
          <div className="flex justify-between">
            <div className="h-3 w-24 bg-gray-100 rounded-full" />
            <div className="h-3 w-20 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
